
import { of as observableOf, Subject, zip } from 'rxjs'

import { TZDate } from '@date-fns/tz'
import IconPrev from '@mui/icons-material/ChevronLeft'
import IconNext from '@mui/icons-material/ChevronRight'
import Button from '@mui/material/Button'
import { InstagramLocationSelector } from 'components/Composer/CustomizePostBlock/InstagramLocationSelector'
import PostPreview from 'components/PostPreview'
import { RecycleSelector } from 'components/Posts/components/RecycleSelector'
import SocialIcon from 'components/SocialIcon'
import { format } from 'date-fns'
import {
  ARTICLE_TYPE,
  NO_TIME_SLOT_VALUE,
  PlannedPost,
  PostBase,
  VIDEO_TYPE
} from 'interfaces'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { switchMap } from 'rxjs/operators'
import { getDerivedStateFromPost, mergeState, resetComposer, updatePost } from 'services/compose'
import { getPostData } from 'services/compose/create/createPost'
import { defaultGoogleEventEndTime, defaultScheduleTimeString } from 'services/compose/state'
import { connectedDestinationsSelector } from 'services/destinations'
import { message } from 'services/snackbar'
import { BRAND_TIKTOK, BRAND_YOUTUBE, BRAND_FACEBOOK, BRAND_PINTEREST, BRAND_TWITTER, BRAND_GOOGLE, BRAND_INSTAGRAM } from 'shared'
import { LocationInfo } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import PostActions from './PostActions'
import styles from './PostPreviewWithInfo.pcss'

interface PostPreviewWithInfoProps {
  post: PlannedPost
  hasPreviousPost: boolean
  hasNextPost: boolean
  className?: string
  onSelectPrevious: () => void
  onSelectNext: () => void
  onDelete: (post: PlannedPost) => void
  onMove?: (post: PlannedPost, position: 'top' | 'bottom') => void
  onEdit: (post: PlannedPost) => void
  onCopy: (post: PlannedPost) => void
  onRecycleChange: (id: string, value: number | boolean | Date, forceRefresh?: boolean) => void
  onClose: () => void
  onPostUpdated?: (post: PlannedPost) => void
  skipDeleteConfirm?: boolean // When true (undo available), delete fires without a confirm dialog
}

export function PostPreviewWithInfo(props: PostPreviewWithInfoProps) {
  const { onPostUpdated } = props
  const dispatch = useDispatch<StoreThunkDispatch>()
  const intl = useIntl()
  const [igLocation, setIgLocation] = useState(props.post.details?.location)
  const profiles = useSelector(connectedDestinationsSelector)
  const postProfile = profiles[props.post.profileId]

  const update$ = useRef<Subject<PostBase>>(undefined)

  useEffect(() => {
    update$.current = new Subject()
    update$.current.pipe(switchMap((post) => {
      dispatch(resetComposer())
      dispatch(mergeState(getDerivedStateFromPost(post)))
      const postData = dispatch(getPostData(post.autoPost ? 'queue' : undefined, !post.autoPost))
      return zip(dispatch(updatePost(undefined, postData, true)), observableOf(post))
    })).subscribe(([_response, post]: any[]) => {
      if (onPostUpdated) {
        onPostUpdated(post)
      }
    }, () => {
      dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
    })

    return () => {
      update$.current?.unsubscribe()
    }
  }, [dispatch, intl, onPostUpdated])

  useEffect(() => {
    if (props.post.network === BRAND_INSTAGRAM) {
      setIgLocation(props.post.details?.location)
    }
  }, [props.post.details?.location, props.post.network])

  const postData = useMemo(() => {
    const data: any = {
      status: {
        text: props.post.status,
        html: props.post.status,
        touched: false
      }
    }
    if ([BRAND_PINTEREST, BRAND_YOUTUBE, BRAND_TIKTOK].includes(props.post.network)) {
      data.title = (props.post as any).title
      data.description = (props.post as any).description
    } else if (props.post.network === BRAND_GOOGLE) {
      data.update = {
        link: props.post.details?.buttonUrl,
        buttonType: props.post.details?.buttonType
      }
      data.event = {
        title: props.post.details?.title || '',
        schedule: {
          startDate: props.post.details?.startDate || defaultScheduleTimeString,
          endDate: props.post.details?.endDate || defaultGoogleEventEndTime
        }
      }
      data.offer = {
        offer: {
          couponCode: props.post.details?.couponCode,
          redeemOnlineUrl: props.post.details?.redeemOnlineUrl || '',
          termsConditions: props.post.details?.termsConditions
        },
        event: {
          title: props.post.details?.title || '',
          schedule: {
            startDate: props.post.details?.startDate || defaultScheduleTimeString,
            endDate: props.post.details?.endDate || defaultGoogleEventEndTime
          }
        }
      }
    } else if (props.post.network === BRAND_INSTAGRAM) {
      data.locations = props.post.details?.location ? { [props.post.details.location.id]: props.post.details.location } : {}
      data.postType = props.post.igPostType
    }
    return data
  }, [props.post])

  const content = useMemo(() => {
    if (props.post.contentType === ARTICLE_TYPE || props.post.contentType === VIDEO_TYPE) {
      return {
        title: props.post.linkTitle,
        description: props.post.linkDescription,
        type: props.post.contentType,
        sourceLink: props.post.linkUrl,
        shortLink: props.post.linkUrl
      } as any
    }
    return undefined
  }, [props.post])

  const profile = useMemo(() => {
    return {
      name: props.post.name,
      image: props.post.profilePictureUrl
    } as any
  }, [props.post.name, props.post.profilePictureUrl])

  const onRecycleChange = (value: number | boolean | Date) => {
    const id = props.post.virtualPostId || props.post.id
    props.onRecycleChange(id, value, props.post.isVirtual)
  }

  const onDelete = () => {
    props.onDelete(props.post)
  }

  const moveToTop = () => {
    if (props.onMove) {
      props.onMove(props.post, 'top')
    }
  }

  const moveToBottom = () => {
    if (props.onMove) {
      props.onMove(props.post, 'bottom')
    }
  }

  const onEdit = () => {
    props.onEdit(props.post)
  }

  const copy = () => {
    props.onCopy(props.post)
  }

  const imageUrls = props.post.images || []

  const postTypeLabel = useMemo(() => {
    if (props.post.isPosted) {
      return 'Posted to: '
    }
    const autoPostLabel = props.post.autoPost ? 'Queued' : 'Scheduled'
    let postType = ''
    switch (props.post.network) {
      case BRAND_FACEBOOK:
        postType = props.post.details?.reshareName ? 'Re-share' : 'Post'
        break
      case BRAND_TWITTER:
        postType = props.post.twPostType === 'quote'
          ? 'Quote tweet'
          : props.post.twPostType === 'retweet' ? 'Retweet' : 'Tweet'
        break
      case BRAND_PINTEREST:
        postType = 'Pin'
        break
      default:
        postType = 'Post'
    }

    return `${autoPostLabel} ${postType} to: `
  }, [props.post.autoPost, props.post.details?.reshareName, props.post.network, props.post.twPostType, props.post.isPosted])

  const recycleValue = useMemo(() => {
    if (typeof props.post.recycle === 'number') {
      return `${props.post.recycle} times`
    }

    if (props.post.recycle === true) {
      return 'Forever'
    }

    if (typeof props.post.recycle === 'object') {
      return `Until ${(props.post.recycle as Date).toLocaleDateString()}`
    }

    return ''
  }, [props.post.recycle])

  const repeatLabel = useMemo(() => {
    if (props.post.autoPost || props.post.repeatLength === 0) {
      return null
    }

    return <FormattedMessage
      id="post.planned.repeat-value-msg"
      values={{
        repeatLength: props.post.repeatLength,
        repeatPeriod: props.post.repeatPeriod,
        repeatUntil: props.post.repeatUntil === 'forever' ? 'Forever' : format(props.post.repeatUntil, 'd LLL yyyy')
      }}
    />
  }, [props.post.repeatLength, props.post.repeatPeriod, props.post.repeatUntil, props.post.autoPost])

  const timeLabel = useMemo(() => {
    if (props.post.timeString === NO_TIME_SLOT_VALUE) {
      return NO_TIME_SLOT_VALUE
    }

    const dateTime = format(new TZDate(props.post.time, props.post.timezone), 'MMMM do, hh:mm a')
    if (props.post.baseTime) {
      return `${dateTime} ish`
    }
    return dateTime
  }, [props.post.timeString, props.post.time, props.post.timezone, props.post.baseTime])

  const onIGLocationChange = (location: LocationInfo | undefined) => {
    setIgLocation(location)
    const post = props.post
    update$.current?.next({ ...post, details: { ...post.details, location } })
  }

  const isPinterest = Boolean(props.post.attributes?.pinterestBoardUrl)
  const attachedDocumentName = props.post.details?.documentName

  return (
    <div className={props.className || ''} data-testid="post-preview-wrapper">
      <div className={styles.nav}>
        <div className={styles.date}>
          {props.hasPreviousPost && (
            <IconPrev className={styles['btn-nav']} role="button" onClick={props.onSelectPrevious} />
          )}
          <div className={styles['date-label']}>
            {timeLabel}
          </div>
          {props.hasNextPost && (
            <IconNext className={styles['btn-nav']} role="button" onClick={props.onSelectNext} />
          )}
        </div>
        <Button variant="text" className={styles['btn-toggle']} onClick={props.onClose}>
          <FormattedMessage id="actions.close" />
        </Button>
      </div>
      <div className={styles['info-box']}>
        <div className={styles['info-line']}>
          <span>
            {postTypeLabel}
          </span>
          <span className={`${styles.profile} text-ellipsis`}>
            <SocialIcon icon={props.post.network} className={styles.icon} circle />
            {props.post.name}
          </span>
        </div>
        {props.post.bucket && (
          <div className={styles['info-line']}>
            <span className="text-ellipsis">
              {`${props.post.autoPost ? 'Queued' : 'Scheduled'} from bucket "${props.post.bucket.name}"`}
            </span>
          </div>
        )}
        {props.post.network === BRAND_INSTAGRAM && !postProfile.instagramDirect && (
          <div className={`${styles['info-line']} ${styles['location-box']}`}>
            <span>
              <FormattedMessage id="label.generic.location" />:
            </span>
            <span>
              <InstagramLocationSelector
                profile={postProfile}
                hideAvatar
                hideLabel
                selectedLocation={igLocation}
                onSelectedLocationChange={onIGLocationChange}
              />
            </span>
          </div>
        )}
        {props.post.details?.photoAlbum && !isPinterest && (
          <div className={styles['info-line']}>
            <span>
              <FormattedMessage id="label.generic.album" />:
            </span>
            <span>
              <a href={`https://www.facebook.com/media/set/?set=a.${props.post.details?.photoAlbum.id}`} target="_blank" rel="noopener">
                {props.post.details?.photoAlbum?.name}
              </a>
            </span>
          </div>
        )}
        {isPinterest && props.post.details?.photoAlbum?.name && (
          <div className={styles['info-line']}>
            <span>
              <FormattedMessage id="label.generic.board" />:
            </span>
            <span>
              <a href={props.post.attributes?.pinterestBoardUrl} target="_blank" rel="noopener">
                {props.post.details.photoAlbum.name}
              </a>
            </span>
          </div>
        )}
        {!props.post.autoPost && props.post.repeatLength > 0 && (
          <div className={styles['info-line']}>
            <span>
              <FormattedMessage id="general.labels.repeat" />:
            </span>
            <span className={styles.util} onClick={onEdit}>{repeatLabel}</span>
          </div>
        )}
        {Boolean(props.post.recycle) && (
          <div className={styles['info-line']}>
            <span>
              <FormattedMessage id="general.labels.recycle" />:
            </span>

            <RecycleSelector
              value={props.post.recycle}
              triggerElement={
                <span className={`${styles['recycle-selector']} ${styles.util}`}>{recycleValue}</span>
              }
              className={styles['recycle-box']}
              onChange={onRecycleChange}
            />
          </div>
        )}
        <div className={styles['info-line']}>
          <span>
            <FormattedMessage id="post.labels.preview-info.created-date" />:
          </span>
          <span>
            {format(props.post.createdAt, 'd LLL yyyy')}
          </span>
        </div>
        {props.post.baseTime && (
          <div className={styles['info-line']}>
            <span>
              <FormattedMessage id="post.labels.preview-info.posts-within" />:
            </span>
            <span>within 20 minutes</span>
          </div>
        )}
      </div>
      <div className={styles['preview-box']}>
        {!props.post.isPosted && !props.post.isVirtual && (
          <PostActions
            showMoveButtons={props.post.autoPost}
            className={styles.actions}
            btnClassName={styles['action-btn']}
            classNameMid={styles['actions-mid']}
            classNameRight={styles['actions-right']}
            skipDeleteConfirm={props.skipDeleteConfirm}
            onEdit={onEdit}
            onDelete={onDelete}
            onMoveToTop={props.onMove ? moveToTop : undefined}
            onMoveToBottom={props.onMove ? moveToBottom : undefined}
            onCopy={copy}
          />
        )}
        <PostPreview
          post={postData}
          tags={props.post.details?.tags}
          imageUrls={imageUrls}
          featuredImageUrl={props.post.nativeVideoUrl ? undefined : imageUrls[0]}
          postNetwork={props.post.network}
          previewProfile={profile}
          videoUrl={props.post.nativeVideoUrl}
          videoThumbnailUrl={props.post.details?.videoThumbnailUrl}
          content={content}
          comments={props.post.postComments?.map(c => c.text).filter(Boolean)}
          reshare={Boolean(props.post.details?.reshareName)}
          textBackgroundUrl={props.post.details?.textBackground?.backgroundUrl}
          textColor={props.post.details?.textBackground?.color}
          igPhotoTags={props.post.details?.photoTags}
          igCoverUrl={props.post.details?.coverUrl}
          documentName={attachedDocumentName}
        />
      </div>
    </div>
  )
}

export default PostPreviewWithInfo
